
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import SmartMeterSummaryStats from './device-health/SmartMeterSummaryStats';
import SmartMeterTable from './device-health/SmartMeterTable';

interface SmartMeter {
  deviceId: string;
  location: string;
  status: string;
  batteryLevel: number;
  signalStrength: number;
  lastCommunication: string;
  firmwareVersion: string;
  readings: {
    voltage: number;
    current: number;
    frequency: number;
    power: number;
    totalEnergy: number;
  };
  maintenanceStatus: {
    lastMaintenance: string;
    nextScheduled: string;
    alerts: string[];
  };
}

interface SmartMeterHealthSummaryProps {
  smartMeters: SmartMeter[];
  isLoading: boolean;
  onSelectDevice: (deviceId: string) => void;
  selectedFilter: string | null;
  onFilterSelect: (filter: string | null) => void;
}

const SmartMeterHealthSummary: React.FC<SmartMeterHealthSummaryProps> = ({ 
  smartMeters,
  isLoading,
  onSelectDevice,
  selectedFilter,
  onFilterSelect
}) => {
  // Calculate summary stats
  const totalMeters = smartMeters.length;
  const healthyMeters = smartMeters.filter(meter => meter.status === 'Healthy').length;
  const warningMeters = smartMeters.filter(meter => meter.status === 'Warning').length;
  const criticalMeters = smartMeters.filter(meter => meter.status === 'Critical').length;

  // Filter smart meters based on selected filter
  const filteredMeters = selectedFilter === null || selectedFilter === 'all'
    ? smartMeters
    : smartMeters.filter(meter => meter.status === selectedFilter);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Smart Meter Health Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-40">
            <p className="text-muted-foreground">Loading data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Smart Meter Health Summary
          <Badge className="ml-2">{totalMeters} Devices</Badge>
          {selectedFilter && selectedFilter !== 'all' && (
            <Badge variant="outline" className="ml-auto">
              Filtered by: {selectedFilter}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <SmartMeterSummaryStats 
          totalMeters={totalMeters}
          healthyMeters={healthyMeters}
          warningMeters={warningMeters}
          criticalMeters={criticalMeters}
          selectedFilter={selectedFilter}
          onFilterSelect={onFilterSelect}
        />

        <SmartMeterTable 
          smartMeters={filteredMeters}
          onSelectDevice={onSelectDevice}
        />
      </CardContent>
    </Card>
  );
};

export default SmartMeterHealthSummary;
